import { format } from 'date-fns';

import type { ProtocolResponse } from 'generated/index';
import type {
  ProtocolValidity,
  ProtocolValidityFilter
} from './Protocols.types';

/**
 * Local calendar date as `yyyy-MM-dd`.
 *
 * Protocol dates are plain calendar dates, so they are compared as ISO strings. Never go through
 * `new Date('2026-09-22')`: that parses as UTC midnight and lands on the wrong local day.
 */
export const toIsoDate = (date: Date): string => format(date, 'yyyy-MM-dd');

export const getProtocolValidity = (
  { validFrom, validUntil }: ProtocolResponse,
  today: Date = new Date()
): ProtocolValidity => {
  const todayIso = toIsoDate(today);

  if (validFrom > todayIso) {
    return 'future';
  }
  if (validUntil && validUntil < todayIso) {
    return 'expired';
  }
  return 'current';
};

const matchesValidityFilter = (
  protocol: ProtocolResponse,
  filter: ProtocolValidityFilter,
  today: Date
) => filter === 'all' || getProtocolValidity(protocol, today) === filter;

const matchesSearch = ({ name }: ProtocolResponse, search: string) =>
  name.toLowerCase().includes(search.trim().toLowerCase());

/** The duration of a protocol is never stored: it is the highest end week of its rules, 0 without rules */
export const getProtocolDurationInWeeks = ({
  rules
}: Pick<ProtocolResponse, 'rules'>): number =>
  rules.reduce(
    (longest, { periods }) =>
      periods.reduce(
        (max, { endPeriodNumber }) => Math.max(max, endPeriodNumber),
        longest
      ),
    0
  );

export const filterProtocols = (
  protocols: ProtocolResponse[],
  search: string,
  filter: ProtocolValidityFilter,
  today: Date = new Date()
): ProtocolResponse[] =>
  protocols.filter(
    (protocol) =>
      matchesSearch(protocol, search) &&
      matchesValidityFilter(protocol, filter, today)
  );
